/**
 * GradientStopEditor — Multi-stop gradient editor for Advanced mode
 *
 * Edits the stops[] array of a GradientOverlay.
 * Each stop has a color picker and position slider.
 * Supports add/remove stops (min 2, max 5).
 */

import React, { useCallback } from 'react';
import { Box, IconButton, Slider, Typography, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import type { GradientStop } from '@segmently/schema-models/v2';
import { ColorInput } from './ColorInput';

const MIN_STOPS = 2;
const MAX_STOPS = 5;

export interface GradientStopEditorProps {
  stops: GradientStop[];
  onChange: (stops: GradientStop[]) => void;
  disabled?: boolean;
}

/**
 * Interpolate a hex color between two colors at a given ratio (0-1).
 */
function interpolateColor(c1: string, c2: string, ratio: number): string {
  const parse = (hex: string) => {
    const h = hex.replace('#', '');
    return [
      parseInt(h.substring(0, 2), 16),
      parseInt(h.substring(2, 4), 16),
      parseInt(h.substring(4, 6), 16),
    ];
  };
  const [r1, g1, b1] = parse(c1);
  const [r2, g2, b2] = parse(c2);
  const r = Math.round(r1 + (r2 - r1) * ratio);
  const g = Math.round(g1 + (g2 - g1) * ratio);
  const b = Math.round(b1 + (b2 - b1) * ratio);
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

export const GradientStopEditor: React.FC<GradientStopEditorProps> = ({
  stops,
  onChange,
  disabled = false,
}) => {
  const sorted = [...stops].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  const handleColorChange = useCallback(
    (index: number, color: string) => {
      const updated = sorted.map((s, i) => (i === index ? { ...s, color } : s));
      onChange(updated);
    },
    [sorted, onChange],
  );

  const handlePositionChange = useCallback(
    (index: number, position: number) => {
      const updated = sorted.map((s, i) =>
        i === index ? { ...s, position: position / 100 } : s,
      );
      // Re-sort by position
      updated.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      onChange(updated);
    },
    [sorted, onChange],
  );

  const handleAddStop = useCallback(() => {
    if (sorted.length >= MAX_STOPS) return;
    // Insert at midpoint between last two stops
    const last = sorted[sorted.length - 1];
    const prev = sorted[sorted.length - 2];
    const midPos = ((prev.position ?? 0) + (last.position ?? 1)) / 2;
    const midColor = interpolateColor(prev.color ?? '#000000', last.color ?? '#ffffff', 0.5);
    const updated = [...sorted, { color: midColor, position: midPos }];
    updated.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    onChange(updated);
  }, [sorted, onChange]);

  const handleRemoveStop = useCallback(
    (index: number) => {
      if (sorted.length <= MIN_STOPS) return;
      const updated = sorted.filter((_, i) => i !== index);
      onChange(updated);
    },
    [sorted, onChange],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {sorted.map((stop, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <ColorInput
            value={stop.color ?? '#000000'}
            onChange={(color) => handleColorChange(index, color as string)}
            disabled={disabled}
          />
          <Slider
            value={Math.round((stop.position ?? 0) * 100)}
            onChange={(_, val) => handlePositionChange(index, val as number)}
            min={0}
            max={100}
            size="small"
            disabled={disabled}
            sx={{ flex: 1 }}
          />
          <Typography
            variant="caption"
            sx={{ minWidth: 28, textAlign: 'right' }}
          >
            {Math.round((stop.position ?? 0) * 100)}%
          </Typography>
          <IconButton
            size="small"
            onClick={() => handleRemoveStop(index)}
            disabled={disabled || sorted.length <= MIN_STOPS}
            sx={{ p: 0.25 }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      ))}
      {sorted.length < MAX_STOPS && (
        <Button
          size="small"
          startIcon={<AddIcon sx={{ fontSize: 14 }} />}
          onClick={handleAddStop}
          disabled={disabled}
          sx={{ alignSelf: 'flex-start', textTransform: 'none', fontSize: 12 }}
        >
          Add Stop
        </Button>
      )}
    </Box>
  );
};
